import { getApiHost } from '@/appSettings';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { Guid } from 'guid-typescript';
import type { AddEmploymentsInput, AddOrganisationsInput, AffectedPeople, AffectedPeopleInput, DiscussionStatus, EmployeeMail, EmploymentDiscussion, EmploymentDiscussionInput, EmploymentOnNode, Manager, MissingEmployment, MissingUser, MissingUserInputs, OrganisationNode, PublishedEmploymentPerformanceReviewStatusIds, PublishedEmploymentPerformanceReviewStatusIdsInputs, PublishedManagerOverviewsInputs, PublishInput, RemoveDiscussionInput, RemoveEmploymentsInput, RemoveOrganisationsInput, SendReminderToEmploymentsInput, SendReminderToOrganisationsInput, UnpublishInput } from '@sysarb/sysarb-types/services/v1/discussion';
import type { useResgate } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';

export class Discussions {
    private readonly discussionsService = new RestManager(`https://${getApiHost()}/discussion-service`);

    public abort(): void {
        this.discussionsService.abort();
    }

    /**
     * Publishes the specific discussion
     * @param publishInput All publish inputs
     */
    public async publish(resgate: ReturnType<typeof useResgate>, publishInput: PublishInput): Promise<unknown> {
        const promise = resgate.call('discussion-service.v1.discussions', 'publish', publishInput).then(res => {
            if (res?.success === false) return null;
            return res.data;
        });
        return promise;
    }

    /**
     * Unpublishes the specific discussion
     * @param unpublishInput All unpublish inputs
     */
    public async unpublish(resgate: ReturnType<typeof useResgate>, unpublishInput: UnpublishInput): Promise<unknown> {
        const promise = resgate.call('discussion-service.v1.discussions', 'unpublish', unpublishInput).then(res => {
            if (res?.success === false) return null;
            return res.data;
        });
        return promise;
    }

    /**
     * Adds employments to the specific discussion
     * @param AddEmploymentsInput All add employments inputs
     */
    public async addEmploymentsToDiscussion(resgate: ReturnType<typeof useResgate>, addEmploymentsInput: AddEmploymentsInput): Promise<unknown> {
        const promise = resgate.call('discussion-service.v1.discussions', 'add-employments-to-discussion', addEmploymentsInput).then(res => {
            if (res?.success === false) return null;
            return res.data;
        });
        return promise;
    }

    /**
     * Remove employments discussions
     * @param RemoveEmploymentsInput All remove employments inputs
     */
    public async removeEmploymentsDiscussions(resgate: ReturnType<typeof useResgate>, removeEmploymentsInput: RemoveEmploymentsInput): Promise<unknown> {
        const promise = resgate.call('discussion-service.v1.discussions', 'remove-employments-discussions', removeEmploymentsInput).then(res => {
            if (res?.success === false) return null;
            return res.data;
        });
        return promise;
    }

    /**
     * Add organisations to the specific discussion
     * @param AddOrganisationsInput All add organisations inputs
     */
    public async addOrganisationsToDiscussion(resgate: ReturnType<typeof useResgate>, addOrganisationsInput: AddOrganisationsInput): Promise<unknown> {
        const promise = resgate.call('discussion-service.v1.discussions', 'add-organisations-to-discussion', addOrganisationsInput);
        return promise;
    }

    /**
     * Remove organisations from the specific discussion
     * @param RemoveOrganisationsInput All remove organisations inputs
     */
    public async removeOrganisationsFromDiscussion(resgate: ReturnType<typeof useResgate>, removeOrganisationsInput: RemoveOrganisationsInput): Promise<unknown> {
        const promise = resgate.call('discussion-service.v1.discussions', 'remove-organisations-from-discussion', removeOrganisationsInput).then(res => {
            if (res?.success === false) return null;
            return res.data;
        });
        return promise;
    }

    /**
     * Remove discussion
     * @param RemoveDiscussionInput All remove discussion inputs
     */
    public async removeDiscussion(resgate: ReturnType<typeof useResgate>, removeDiscussionInput: RemoveDiscussionInput): Promise<unknown> {
        const promise = resgate.call('discussion-service.v1.discussions', 'remove-discussion', removeDiscussionInput).then(res => {
            if (res?.success === false) return null;
            return res.data;
        });
        return promise;
    }

    /**
     * Archive/complete discussion
     * @param discussionId The id of the discussion to archive/complete
     */
    public async archiveDiscussion(resgate: ReturnType<typeof useResgate>, discussionId: string | Guid): Promise<unknown> {
        const promise = resgate.call(`discussion-service.v1.discussions.${discussionId}`, 'archive', {}).then(res => {
            if (res?.success === false) return null;
            return res.data;
        });
        return promise;
    }

    /**
     * Revert archivation of discussion
     * @param discussionId The id of the discussion to revert archivation on
     */
    public async revertArchiveDiscussion(resgate: ReturnType<typeof useResgate>, discussionId: string | Guid): Promise<unknown> {
        const promise = resgate.call(`discussion-service.v1.discussions.${discussionId}`, 'revertarchive', {});
        return promise;
    }

    /**
     * Get the number of discussions that will have their status reverted
     * @param discussionId The id of the discussion to get status from
     */
    public async getRevertedDiscussions(discussionId: string | Guid): Promise<{ count: number }> {
        const { instance } = useLoginStore();
        return this.discussionsService.get<{ count: number }>(`v1/${instance}/discussions/${discussionId}/revertstatuscount`);
    }

    /**
     * Get status of the selected discussion
     * @param discussionId The id of the discussion to get status from
     */
    public async getDiscussionStatus(discussionId: string | Guid): Promise<DiscussionStatus> {
        const { instance } = useLoginStore();
        return this.discussionsService.get<DiscussionStatus>(`v1/${instance}/discussions/${discussionId}/status`);
    }

    /**
     * Get a list of all organisation nodes
     */
    public async getOrganisation(): Promise<OrganisationNode[]> {
        const { instance } = useLoginStore();
        return this.discussionsService.get<OrganisationNode[]>(`v1/${instance}/discussions/organisationNodes`);
    }

    /**
     * Get managers of the selected discussion
     * @param discussionId The id of the discussion to get managers from
     */
    public async getManagers(discussionId: string | Guid): Promise<Manager[]> {
        const { instance } = useLoginStore();
        return this.discussionsService.get<Manager[]>(`v1/${instance}/discussions/${discussionId}/managers`);
    }

    /**
     * Get employment discussions of the selected discussion and organisation node
     * @param employmentDiscussionInput The employmentDiscussionInput
     */
    public async getEmploymentDiscussions(employmentDiscussionInput: EmploymentDiscussionInput): Promise<EmploymentDiscussion[]> {
        const { instance } = useLoginStore();
        return this.discussionsService.post<EmploymentDiscussion[]>(employmentDiscussionInput, `v1/${instance}/discussions/employmentDiscussions`);
    }

    /**
     * Get affected people
     * @param affectedPeopleInput The AffectedPeopleInput
     */
    public async getAffectedPeople(affectedPeopleInput: AffectedPeopleInput): Promise<AffectedPeople> {
        const { instance } = useLoginStore();
        return this.discussionsService.post<AffectedPeople>(affectedPeopleInput, `v1/${instance}/discussions/getAffectedPeople`);
    }

    /**
     * Get employments on the selected discussion and node
     * @param discussionId The id of the discussion to get the employments from
     * @param organisationNodeId The id of the organisation to get the employments from
     */
    public async getEmploymentsOnNode(discussionId: string | Guid, organisationNodeId: string | Guid): Promise<EmploymentOnNode[]> {
        const { instance } = useLoginStore();
        return this.discussionsService.get<EmploymentOnNode[]>(`v1/${instance}/getEmploymentsOnNode/${discussionId}/getEmploymentsOnNode${organisationNodeId}`);
    }

    /**
     * Send reminder to employments
     * @param sendReminderToEmploymentsInput The SendReminderToEmploymentsInput
     */
    public async sendReminderToEmployments(sendReminderToEmploymentsInput: SendReminderToEmploymentsInput): Promise<EmployeeMail> {
        const { instance } = useLoginStore();
        return this.discussionsService.post<EmployeeMail>(sendReminderToEmploymentsInput, `v1/${instance}/employments/sendReminder`);
    }

    /**
     * Send reminder to organisations
     * @param sendReminderToOrganisationsInput The SendReminderToOrganisationsInput
     */
    public async sendReminderToOrganisations(sendReminderToOrganisationsInput: SendReminderToOrganisationsInput): Promise<EmployeeMail> {
        const { instance } = useLoginStore();
        return this.discussionsService.post<EmployeeMail>(sendReminderToOrganisationsInput, `v1/${instance}/discussions/sendReminder`);
    }

    /**
     * Get number of answers on the selected discussion
     * @param discussionId The id of the discussion to get status from
     */
    public async numberOfAnswersOnDiscussion(discussionId: string | Guid): Promise<{ count: number }> {
        const { instance } = useLoginStore();
        return this.discussionsService.get<{ count: number }>(`v1/${instance}/discussions/${discussionId}/numberOfAnswersOnDiscussion`);
    }

    /**
     * Get number of answers on the selected discussion
     * @param questionId The id of the question (adminOverviewQuestion)
     */
    public async numberOfAnswersOnQuestion(questionId: string | Guid): Promise<{ count: number }> {
        const { instance } = useLoginStore();
        return this.discussionsService.get<{ count: number }>(`v1/${instance}/discussions/${questionId}/numberOfAnswersOnQuestion`);
    }

    /**
     * Get missing employees of the selected discussion
     * @param discussionId The id of the discussion to get missing employees from
     */
    public async getMissingEmployments(discussionId: string | Guid): Promise<MissingEmployment[]> {
        const { instance } = useLoginStore();
        return this.discussionsService.get<MissingEmployment[]>(`v1/${instance}/discussions/${discussionId}/missingEmployees`);
    }

    /**
     * Get published employment peformance review ids from a specific employee
     * @param employeeId The id of the employee
     */
    public async publishedEmploymentPerformanceReviews(employeeId: string | Guid): Promise<string[]> {
        const { instance } = useLoginStore();
        return this.discussionsService.get<string[]>(`v1/${instance}/managersAndCoworkers/${employeeId}/publishedEmploymentPerformanceReviews`);
    }

    /**
     * Get published employment peformance manager overview ids
     * @param publishedManagerOverviewsInputs The PublishedManagerOverviewsInputs
     */
    public async publishedEmploymentPerformanceManagerOverviews(publishedManagerOverviewsInputs: PublishedManagerOverviewsInputs): Promise<string[]> {
        const { instance } = useLoginStore();
        return this.discussionsService.post<string[]>(publishedManagerOverviewsInputs, `v1/${instance}/managersAndCoworkers/publishedEmploymentPerformanceManagerOverviews`);
    }

    /**
     * Get published employment peformance review status ids
     * @param publishedEmploymentPerformanceReviewStatusIdsInputs The PublishedEmploymentPerformanceReviewStatusIdsInputs
     */
    public async publishedEmploymentPerformanceReviewStatusIds(publishedEmploymentPerformanceReviewStatusIdsInputs: PublishedEmploymentPerformanceReviewStatusIdsInputs): Promise<PublishedEmploymentPerformanceReviewStatusIds> {
        const { instance } = useLoginStore();
        return this.discussionsService.post<PublishedEmploymentPerformanceReviewStatusIds>(publishedEmploymentPerformanceReviewStatusIdsInputs, `v1/${instance}/managersAndCoworkers/publishedEmploymentPerformanceReviewStatusIds`);
    }

    /**
     * Get employees without users
     * @param input The ids of the organisation nodes to user data for
     */
    public async getEmployeesWithoutUsers(input: MissingUserInputs): Promise<MissingUser[]> {
        const { instance } = useLoginStore();
        return this.discussionsService.post<MissingUser[]>(input, `v1/${instance}/employments/missingusers`);
    }

    /**
     * Download a discussion file by its ID
     * @param fileId The ID of the file to download
     * @returns The file as an ArrayBuffer
     */
    public async getDiscussionFile(fileId: string | Guid): Promise<ArrayBuffer> {
        const { instance } = useLoginStore();
        return this.discussionsService.get<ArrayBuffer>(`v1/${instance}/discussion-files/${fileId}`, undefined, { responseType: 'arraybuffer' });
    }
}
